import { TZDateMini } from '@date-fns/tz';
import { startOfDay } from 'date-fns';

import { getTimezone } from '@dynatrace-sdk/user-preferences';

/**
 * Defines the Grail min date.
 * @internal
 */
export const GRAIL_MIN_DATE = '1677-09-21T00:12:43.145224192Z';
/**
 * Defines the Grail max date.
 * @internal
 */
export const GRAIL_MAX_DATE = '2262-04-11T23:47:16.854775807Z';

/** Get formatted date. */
function getISOFormattedDate(date: Date): string {
  return `${valueTo4DigitString(date.getFullYear())}-${valueTo2DigitString(
    date.getMonth() + 1,
  )}-${valueTo2DigitString(date.getDate())}`;
}

/** Get formatted time. */
function getISOFormattedTime(date: Date): string {
  return `${valueTo2DigitString(date.getHours())}:${valueTo2DigitString(
    date.getMinutes(),
  )}:${valueTo2DigitString(date.getSeconds())}`;
}

/**
 * Get the formatted localized date/time.
 * @public
 */
export function getFormattedDateTime(date: Date): string {
  return `${getISOFormattedDate(date)} ${getISOFormattedTime(date)}`;
}

/**
 * Get milli-/micro-/nanoseconds string from string.
 * @internal
 */
export function getSecondFractions(searchString: string | null): string {
  return searchString && searchString.includes('.')
    ? searchString.split('.')[1].replace('Z', '')
    : '';
}

/**
 * Format a number with max two digits to always display two digits
 * (with a leading 0 in case it is a single digit or convert it to string otherwise).
 */
function valueTo2DigitString(value: number): string {
  return `${value}`.padStart(2, '0');
}

/**
 * Format a number with max four digits to always display four digits
 * (with a leading zeros in case it is under 1000 or convert it to string otherwise).
 */
function valueTo4DigitString(value: number): string {
  return `${value}`.padStart(4, '0');
}

/**
 * Replace the milli/micro/nano seconds part.
 * @returns Simplified extended ISO string
 * @internal
 */
export function replaceSecondFractions(
  /** Date-time-string in simplified extended ISO format (Zulu time) */
  searchString: string,
  /** Fractions of a second, without ending 'Z' */
  milliMicroNanoSeconds?: string | null,
): string {
  if (milliMicroNanoSeconds) {
    return `${searchString.replace(
      searchString.split('.')[1],
      milliMicroNanoSeconds,
    )}Z`;
  }
  return searchString;
}

/** Get time part from iso string. */
function getTimeFromISOString(isoDate: string | null): string {
  return isoDate ? isoDate.split('T')[1].replace('Z', '') : '';
}

/**
 * Replace time of given ISO date string with given time.
 * @internal
 */
export function applyInitialTime(
  newDate: string,
  originalDate: string | null,
  milliMicroNanoSeconds: string = getTimeFromISOString(
    startOfDay(TZDateMini.tz(getTimezone())).toISOString(),
  ).split('.')[1],
): string {
  if (!originalDate) {
    return replaceSecondFractions(newDate, milliMicroNanoSeconds);
  }

  const configuredTimezone = getTimezone();

  const datePart = new TZDateMini(newDate, configuredTimezone);
  const timePart = new TZDateMini(originalDate, configuredTimezone);

  // Combine the date part and the time part
  datePart.setMilliseconds(timePart.getMilliseconds());
  datePart.setUTCSeconds(timePart.getUTCSeconds());
  datePart.setMinutes(timePart.getMinutes());
  datePart.setHours(timePart.getHours());

  return datePart.toISOString();
}
